/**
 * WS-NAV — the workspace definition module.
 *
 * ONE config drives the switcher, the sidebar tree, the Home cards, the
 * keyboard shortcuts and the deep-link → workspace resolution. Adding a nav
 * entry is a one-line edit here; nothing else knows the IA.
 *
 * Source: `.vibe/DATA_PIPELINES/AERIOS_DEFAULT/workspaces_and_pilot_wizard.md`
 * §1 (workspace list + roles), §2 (branch contents), §2a (2026-08-11 review
 * decisions: Standby & Time Tracking, Notification rules, Statistics
 * placement, "Import AI Findings stays in Administration").
 *
 * Documented deviations from §2 (see the PR description / story):
 *   - **Analysis → "Inspections" is shipped as "Data Acquisitions".** §2 lists
 *     both, but story 051 renamed the one list they both refer to; two rows
 *     pointing at `/data-acquisitions` inside one tree would be two doors into
 *     the same room. The tree carries the page's real name. Projects keeps its
 *     own Data Acquisitions entry (the PM state-tracking door).
 *   - **Assets does not open on a Fleet map.** The fleet map ships as a
 *     Dashboard widget (MAP-1..4), not a standalone route; building a page for
 *     it is a rebuild, and this refactor is a re-grouping. Assets opens on
 *     Turbines (the primary asset) until a fleet-map route exists.
 *   - **"(later)" items are omitted, "(stub)" items ship.** Devices and Defect
 *     evolution are not in the tree; Statistics and the four Field Data pages
 *     are coming-soon stub pages.
 */
import type { Role } from "@aeriosiq/ui";
import { DroneIcon } from "./icons.js";
import type { Workspace, WorkspaceId } from "./types.js";
import {
  BoxIcon,
  FolderIcon,
  GlobeIcon,
  ScanSearchIcon,
  ShieldCheckIcon,
} from "@aeriosiq/ui/icons";

/** Every role in the platform enum (`auth/v1` `Role`). */
export const ALL_ROLES: readonly Role[] = [
  "platform_admin",
  "tenant_admin",
  "project_manager",
  "back_office",
  "pre_screener",
  "expert_analyst",
  "pilot",
];

/** Every role except `pilot` — the "office" roles. */
const OFFICE_ROLES: readonly Role[] = ALL_ROLES.filter((r) => r !== "pilot");

export const HOME_PATH = "/home";

export const WORKSPACES: readonly Workspace[] = [
  {
    id: "field-data",
    label: "Field Data",
    tagline: "I have new inspection data",
    // Top-down quadcopter, not an airliner — see `icons.tsx`.
    Icon: DroneIcon,
    // §1: pilot (+ all). Everyone can hand data in.
    roles: ALL_ROLES,
    startPath: "/field-data/upload",
    shortcut: 1,
    items: [
      { label: "Upload", to: "/field-data/upload" },
      { label: "My Intake", to: "/field-data/intake" },
      // DI-3-D — the finished data sets (Intake Mockups board 5)
      { label: "Intake History", to: "/field-data/intake/history" },
      { label: "My tasks", to: "/field-data/tasks" },
      { label: "Standby & Time Tracking", to: "/field-data/standby" },
    ],
    ownedPathPrefixes: ["/field-data"],
  },
  {
    id: "projects",
    label: "Projects",
    tagline: "Plan & manage inspection projects",
    Icon: FolderIcon,
    roles: ["project_manager", "back_office", "tenant_admin", "platform_admin"],
    startPath: "/",
    shortcut: 2,
    items: [
      // §2 W2: the global Dashboard becomes this workspace's start page.
      { label: "Overview", to: "/" },
      { label: "Projects", to: "/projects" },
      { label: "Contracts", to: "/contracts" },
      { label: "Contract Positions", to: "/contract-positions" },
      { label: "Inspection Instructions", to: "/inspection-instructions" },
      // The PM door into datasets — state tracking of intake / delivery.
      { label: "Data Acquisitions", to: "/data-acquisitions" },
      // §2a: final inspection reports are a Projects deliverable. Still a stub.
      { label: "Reports", to: "/reports", enabled: false },
    ],
    ownedPathPrefixes: ["/projects", "/contracts", "/contract-positions"],
  },
  {
    id: "analysis",
    label: "Analysis",
    tagline: "Analyze inspections & findings",
    Icon: ScanSearchIcon,
    roles: ["expert_analyst", "pre_screener", "tenant_admin", "platform_admin"],
    startPath: "/data-acquisitions",
    shortcut: 3,
    items: [
      { label: "Data Acquisitions", to: "/data-acquisitions" },
      // Two doors, one route: viewing here, managing in Administration.
      { label: "Defect Catalogues", to: "/defect-catalogues" },
      // §2a: the fleet-analytics tool carried over from the current frontend.
      { label: "Statistics", to: "/statistics" },
    ],
    ownedPathPrefixes: [
      "/analysis",
      "/data-acquisitions",
      "/defect-catalogues",
    ],
  },
  {
    id: "assets",
    label: "Assets",
    tagline: "Fleet, turbines & components",
    Icon: BoxIcon,
    roles: [
      "project_manager",
      "back_office",
      "expert_analyst",
      "pre_screener",
      "tenant_admin",
      "platform_admin",
    ],
    startPath: "/turbines",
    shortcut: 4,
    items: [
      { label: "Parks & Sites", to: "/sites" },
      { label: "Turbines", to: "/turbines" },
      { label: "Turbine Models", to: "/turbine-models" },
      { label: "Components", to: "/components" },
      { label: "Component Models", to: "/component-models" },
    ],
    ownedPathPrefixes: [
      "/sites",
      "/turbines",
      "/turbine-models",
      "/components",
      "/component-models",
    ],
  },
  {
    id: "administration",
    label: "Administration",
    group: "admin",
    tagline: "Users, settings & configuration",
    Icon: ShieldCheckIcon,
    // §1 names tenant_admin as the primary role, but the address book is
    // readable by every non-pilot role (CM-D1). Offering the workspace to all
    // office roles + filtering the items keeps Contacts reachable exactly as
    // it was before the refactor; a role with zero permitted items never sees
    // the workspace at all (see `visibleWorkspaces`).
    roles: OFFICE_ROLES,
    startPath: "/admin/users",
    shortcut: 5,
    items: [
      {
        section: "Access",
        label: "Users",
        to: "/admin/users",
        requiresTenantAdmin: true,
      },
      {
        section: "Access",
        label: "Contacts",
        to: "/admin/contacts",
        requiresContactRead: true,
      },
      {
        section: "Access",
        label: "Partner Companies",
        to: "/admin/partner-companies",
        requiresTenantAdmin: true,
      },
      {
        section: "Access",
        label: "Audit log",
        to: "/admin/audit-log",
        requiresTenantAdmin: true,
      },
      // §2a missing-feature #2: standby notifications are configured per
      // customer/project here (first case: standby time → email to Nordex).
      {
        section: "Settings",
        label: "Notification rules",
        to: "/admin/notification-rules",
        requiresTenantAdmin: true,
      },
      {
        section: "Settings",
        label: "Limits",
        to: "/settings/limits",
        requiresTenantAdmin: true,
      },
      {
        section: "Settings",
        label: "Billing",
        to: "/settings/billing",
        enabled: false,
      },
      // §2 W5: config is admin work; viewing happens in Analysis.
      {
        section: "Configuration",
        label: "Defect Catalogues",
        to: "/defect-catalogues",
      },
      {
        section: "Configuration",
        label: "Defect Type Collections",
        to: "/account/defect-collections",
      },
      { section: "Configuration", label: "AI Models", to: "/ai-models" },
      // §2a confirmation: Import AI Findings stays in Administration.
      {
        section: "Configuration",
        label: "Import AI Findings",
        to: "/ai-findings-import",
      },
      {
        section: "Configuration",
        label: "Auto-Group Rules",
        to: "/auto-group-rules",
        enabled: false,
      },
    ],
    ownedPathPrefixes: [
      "/admin",
      "/settings",
      "/ai-models",
      "/ai-findings-import",
      "/account/defect-collections",
      // Shared with Analysis — whichever workspace is already active keeps it
      // (the "same route, two doors" rule; see `resolveActiveWorkspaceId`).
      "/defect-catalogues",
    ],
  },
  {
    id: "platform",
    label: "Platform",
    group: "admin",
    tagline: "Cross-tenant platform staff tools",
    Icon: GlobeIcon,
    roles: ["platform_admin"],
    startPath: "/platform-admin/users",
    accent: "platform",
    shortcut: 6,
    items: [
      { label: "Users", to: "/platform-admin/users" },
      { label: "Bug reports", to: "/platform-admin/bug-reports" },
    ],
    ownedPathPrefixes: ["/platform-admin"],
  },
];

export function workspaceById(id: string): Workspace | null {
  return WORKSPACES.find((w) => w.id === id) ?? null;
}

/**
 * Doc §1, "Default landing for" — the workspace a role belongs to when
 * nothing else decides. Used only as a DISPLAY fallback: which tree to show
 * while the user sits on a route no workspace owns (the Dashboard for a
 * non-PM, an account page). Without it an analyst opening `/` would be shown
 * the Field Data tree purely because it is first in the list.
 */
const PREFERRED_BY_ROLE: Readonly<Partial<Record<Role, WorkspaceId>>> = {
  pilot: "field-data",
  project_manager: "projects",
  back_office: "projects",
  expert_analyst: "analysis",
  pre_screener: "analysis",
  tenant_admin: "administration",
  platform_admin: "platform",
};

export function preferredWorkspaceId(role: Role): WorkspaceId | null {
  return PREFERRED_BY_ROLE[role] ?? null;
}
